import { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import noop from 'lodash/noop';

import { useEnvironmentContext } from '@sbv-clients/data-access-api';
import {
  Alert,
  Box,
  Card,
  CircularProgress,
  GridLegacy,
  Typography,
} from '@sbv-clients/ui-shared';
import { getApiErrorMessage } from '@sbv-clients/util-misc';

import FacilityForm, {
  FacilityFormValues,
} from '../../components/facility-form';
import { useOnboardAdminFacility } from '../../services/admin';
import { useAdminFacilityByDatabaseNameGetWithMutation } from '../../services/admin-facility';

export const OnboardFacilityPage = () => {
  const navigate = useNavigate();
  const { facilityName } = useParams<{ facilityName: string }>();
  const [searchParams] = useSearchParams();
  const adminFacilityByDatabaseNameGetWithMutation =
    useAdminFacilityByDatabaseNameGetWithMutation();
  const { environment } = useEnvironmentContext();
  const isProd = environment === 'prod';
  const onboardAdminFacility = useOnboardAdminFacility();

  const data = adminFacilityByDatabaseNameGetWithMutation?.data;
  const { isIdle, isLoading, isError, error } =
    adminFacilityByDatabaseNameGetWithMutation;

  const teamSupportId = searchParams.get('teamSupportId') as string;
  const defaultValues = useMemo(() => {
    const body = {
      name: facilityName,
      desktopUserName: '',
      databaseName: data?.databaseName ?? '',
      databaseHost: data?.databaseHost ?? '',
      databaseUser: data?.databaseUser ?? '',
      databasePassword: data?.databasePassword ?? '',
      databaseEncrypt: false,
      licenseKey: data?.licenseKey ?? '',
      teamSupportID: data?.teamSupportId ?? '',
      timezone: '',
      usingMobile: false,
      usingSbvPro: false,
      usingSaaSAuthentication: false,
    } as FacilityFormValues;

    if (!isProd) {
      body.teamSupportID = teamSupportId;
    }
    return body;
  }, [
    data?.databaseHost,
    data?.databaseName,
    data?.databasePassword,
    data?.databaseUser,
    data?.licenseKey,
    data?.teamSupportId,
    facilityName,
    isProd,
    teamSupportId,
  ]);

  const onSubmit = useCallback(
    async (formValues: FacilityFormValues) => {
      try {
        await onboardAdminFacility.mutateAsync(formValues);
        navigate('/facility');
        // eslint-disable-next-line no-empty
      } catch (e) {}
    },
    [onboardAdminFacility, navigate]
  );

  useEffect(() => {
    if (!facilityName || !isProd) {
      return;
    }
    adminFacilityByDatabaseNameGetWithMutation
      .mutateAsync(facilityName as string)
      .catch(noop);
    // eslint-disable-next-line
  }, [facilityName]);

  if ((isIdle || isLoading) && isProd) {
    return (
      <Box p={4} display="flex" justifyContent="center" alignItems="center">
        <CircularProgress />
      </Box>
    );
  }

  if (isError && isProd) {
    return <Alert severity="error">{getApiErrorMessage(error)}</Alert>;
  }

  return (
    <GridLegacy container justifyContent="center">
      <GridLegacy item xs={12} sm={6}>
        <Card>
          <Box p={8}>
            <Typography variant="h3" mb={8}>
              Facility Information
            </Typography>
            <FacilityForm
              defaultValues={defaultValues}
              mode="create"
              actionTitle="Submit"
              onSubmit={onSubmit}
            />
          </Box>
        </Card>
      </GridLegacy>
    </GridLegacy>
  );
};
